import { take, fork, cancel, call, put, select, delay } from 'redux-saga/effects';
import dayjs from 'dayjs';
import axios from '../axiosConfig';
import {
  tourAvailabilityRequest,
  tourAvailabilitySuccess,
  tourAvailabilityFail,
} from '../../slices/tour';
import getTourQuoteKey from '../../../helpers/getTourQuoteKey';

const QuoteDebounceMs = 300;

function* tourAvailabilityWorker({
  payload: {
    productId,
    date,
    rangeDays,
    rateQuantities,
    slotDescription,
    isCalendar,
    doReplaceDays,
  },
}) {
  try {
    const search = yield select((store) => store.tripSearch.searchObj.tour);
    const { currencyCode } = yield select((store) => store.tripSearch);
    // The calendar always asks for a whole month, past days included: a fixed window is one cache key
    // shared by everyone all month, where starting at today would mint a new key every day. The past
    // costs nothing — the provider returns no days for it and the server drops anything before today.
    const month = dayjs(date || search.startDate).startOf('month');
    const body = {
      productId,
      date: isCalendar ? month.format('YYYY-MM-DD') : date,
      rangeDays: isCalendar ? month.daysInMonth() - 1 : rangeDays || 0,
      currency: currencyCode || undefined,
      rateQuantities,
      slotDescription,
    };
    const quoteKey = isCalendar ? undefined : getTourQuoteKey(body);

    if (!isCalendar) {
      const details = yield select((store) => store.tour.details[productId]);
      if (details && details.quote && details.quoteKey === quoteKey) {
        yield put(
          tourAvailabilitySuccess({
            productId,
            response: details.quote,
            isCalendar,
            quoteKey,
            month: month.format('YYYY-MM'),
          }),
        );
        return;
      }
    }

    const { data } = yield call(axios, { method: 'post', url: '/tours/availability', data: body });
    yield put(
      tourAvailabilitySuccess({
        productId,
        response: data,
        isCalendar,
        doReplaceDays,
        quoteKey,
        month: month.format('YYYY-MM'),
      }),
    );
  } catch (error) {
    yield put(tourAvailabilityFail({ productId, error: `${error}`, isCalendar }));
  }
}

function* quoteAfterDelay(action) {
  yield delay(QuoteDebounceMs);
  yield call(tourAvailabilityWorker, action);
}

// Every open tour card asks for its own dates and prices, so a request may only supersede an
// earlier one for the same product: cancelling across products left those cards loading forever.
function* watchPerProduct(isMatch, run) {
  const running = {};

  while (true) {
    const action = yield take(isMatch);
    const { productId } = action.payload;
    if (running[productId]) {
      yield cancel(running[productId]);
    }
    running[productId] = yield fork(run, action);
  }
}

const isRequest = (action) => action.type === tourAvailabilityRequest.toString();

function* tourAvailabilityWatcher() {
  yield fork(
    watchPerProduct,
    (action) => isRequest(action) && !action.payload.isCalendar,
    quoteAfterDelay,
  );
  yield fork(
    watchPerProduct,
    (action) => isRequest(action) && action.payload.isCalendar,
    tourAvailabilityWorker,
  );
}

export default tourAvailabilityWatcher;
